import { Outlet } from "react-router";
import { useMe } from "../../hooks/useMe";
import { ForbiddenPage } from "../../routes/ForbiddenPage";

/**
 * Feature IDs a route can be gated on. Each maps to an install-wide feature
 * flag delivered on the auth payload (`appConfig.features`). These ids match
 * the `requiresFeature` values used by the sidebar navigation so a route and
 * its nav entry stay gated on the same flag.
 */
export type GatedFeature = "inter-instance-transfer" | "wsdashboard" | "webconsole";

type RequireFeatureProps = {
  feature: GatedFeature;
};

/**
 * Route guard that renders the forbidden page when an install-wide feature
 * flag is off. Fails closed: an absent/still-loading feature map is treated as
 * off, so a disabled feature is never reachable by direct URL entry. Compose
 * with `RequireRole` (outer) so a route requires both authorization and the
 * feature being enabled.
 */
export const RequireFeature = ({ feature }: RequireFeatureProps) => {
  const { appConfig } = useMe();
  const features = appConfig?.features;

  let enabled = false;
  switch (feature) {
    case "inter-instance-transfer":
      enabled = features?.interInstanceTransferEnabled ?? false;
      break;
    case "wsdashboard":
      enabled = features?.wsdashboardEnabled ?? false;
      break;
    case "webconsole":
      enabled = features?.webconsoleEnabled ?? false;
      break;
  }

  if (!enabled) {
    return <ForbiddenPage />;
  }

  return <Outlet />;
};
